<template>
  <div>
    <label class="input-label">Title</label>
    <div class="d-flex align-items-center">
      <input
        v-model="title"
        type="text"
        class="form-control form-control-sm optimizely-meta-title-input"
      >
      <button
        v-if="languageDetectorSupported"
        type="button"
        class="btn btn-link btn-sm ml-1"
        :disabled="detectingLanguage"
        title="Detect language and country from instance text"
        @click="detectLanguageAndCountry"
      >
        <i :class="detectingLanguage ? 'fa fa-spinner fa-spin' : 'fa fa-magic'" />
      </button>
    </div>

    <div
      v-if="loadingFields"
      class="text-center mt-2"
    >
      <i class="fa fa-spinner fa-spin" />
    </div>

    <div
      v-else
      class="row mt-2"
    >
      <div
        v-for="field in textFields"
        :key="field.id"
        class="col-md-6 mt-2"
      >
        <label class="input-label">{{ field.name }}</label>
        <input
          v-model="selected[field.id]"
          type="text"
          class="form-control"
        >
        <input
          type="hidden"
          class="optimizely-meta-field-input"
          :data-field-id="field.id"
          :value="serializeSelection(field)"
        >
      </div>
    </div>

    <div
      v-if="!loadingFields"
      class="row mt-2"
    >
      <div
        v-for="field in dateFields"
        :key="field.id"
        class="col-md-6 mt-2"
      >
        <label class="input-label">{{ field.name }}</label>
        <input
          v-model="selected[field.id]"
          type="date"
          class="form-control"
        >
        <input
          type="hidden"
          class="optimizely-meta-field-input"
          :data-field-id="field.id"
          :value="serializeSelection(field)"
        >
      </div>
    </div>

    <div
      v-if="!loadingFields"
      class="row mt-2"
    >
      <div
        v-for="field in selectFields"
        :key="field.id"
        class="col-md-6 mt-2"
      >
        <label class="input-label">{{ field.name }}</label>
        <Multiselect
          v-model="selected[field.id]"
          :options="field.choices"
          :multiple="field.is_multi_select"
          track-by="id"
          label="name"
        />
        <input
          type="hidden"
          class="optimizely-meta-field-input"
          :data-field-id="field.id"
          :value="serializeSelection(field)"
        >
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { OPTIMIZELY_EVENTS, optimizelyEventBroker } from '@frontend/group/modules/optimizely/event-broker';
import { getOptimizelyTemplateByInstanceId } from '@frontend/group/modules/publish-utils/publish-utils';
import { COUNTRY_CODES, LANGUAGE_CODES, nameToCode, defaultCountryForLanguage } from '@frontend/constants/iso-codes';
import { FIELD_TYPE_TEXT, FIELD_TYPE_MULTI_LINE_TEXT } from '@frontend/constants/type-fields-of-template';
import { ANSELL_FIELD_SLUGS, ANSELL_DATA_KEYS } from '@frontend/constants/optimizely';

const fieldsCache = {};
const inFlightRequests = {};

const normalizeBrandName = (name) => name.replace(/[™®©]/g, '').toLowerCase().trim();

const TITLE_TOKENS = {
    region: { token: '[Region]', codes: null },
    country: { token: '[CC]', codes: COUNTRY_CODES },
    languages: { token: '[LANG]', codes: LANGUAGE_CODES }
};

async function fetchManualFields(platformId) {
    if (fieldsCache[platformId]) {
        return fieldsCache[platformId];
    }

    if (!inFlightRequests[platformId]) {
        inFlightRequests[platformId] = axios.post('/optimizely/meta-fields', { platform_id: platformId });
    }

    try {
        const { data } = await inFlightRequests[platformId];

        if (data.status === 'error') {
            throw new Error(data.message);
        }

        fieldsCache[platformId] = data.data || [];
    } finally {
        delete inFlightRequests[platformId];
    }

    return fieldsCache[platformId];
}

export default {
    name: "MetaFields",
    components: { Multiselect },
    data() {
        return {
            title: '[Item]_Technical-Data-Sheet_[Region]_[CC]_[LANG]',
            fields: [],
            selected: {},
            titleTokenValues: {},
            detectingLanguage: false,
            loadingFields: false,
            instanceId: null
        }
    },
    computed: {
        textFields() {
            return this.fields.filter((field) => field.type === 'text');
        },
        dateFields() {
            return this.fields.filter((field) => field.type === 'date');
        },
        selectFields() {
            return this.fields.filter((field) => field.type !== 'text' && field.type !== 'date');
        },
        languageDetectorSupported() {
            return typeof window !== 'undefined' && 'LanguageDetector' in window;
        }
    },
    watch: {
        selected: {
            deep: true,
            handler() {
                this.updateTitleTokens();
            }
        }
    },
    mounted() {
        this.instanceId = $(this.$el).closest('[data-instance-id]').data('instance-id');

        optimizelyEventBroker.on(OPTIMIZELY_EVENTS.HANDLE_META_FIELDS, this.loadFields);
    },
    beforeDestroy() {
        optimizelyEventBroker.off(OPTIMIZELY_EVENTS.HANDLE_META_FIELDS, this.loadFields);
    },
    methods: {
        async loadFields({ platformId }) {
            if (!platformId) {
                return;
            }

            this.loadingFields = true;

            try {
                this.fields = await fetchManualFields(platformId);

                this.fields.forEach((field) => {
                    if (this.isPlainInput(field)) {
                        this.$set(this.selected, field.id, field.default[0] || '');

                        return;
                    }

                    const defaultIds = field.is_multi_select ? field.default : [field.default[0]];
                    const defaultChoices = field.choices.filter((choice) => defaultIds.includes(choice.id));

                    this.$set(this.selected, field.id, field.is_multi_select ? defaultChoices : (defaultChoices[0] || null));
                });

                this.updateManualFields();
                this.updateItemToken();
                this.updateTitleTokens();
            } finally {
                this.loadingFields = false;
            }
        },

        updateManualFields() {
            const template = getOptimizelyTemplateByInstanceId(this.instanceId);
            const preview = this.getPreview();

            const brandField = this.fields.find((field) => field.slug === ANSELL_FIELD_SLUGS.PRODUCT_BRAND);
            const brandName = template?.[ANSELL_DATA_KEYS.MARKETING_PRODUCT_BRAND] || preview?.data?.[ANSELL_DATA_KEYS.MARKETING_PRODUCT_BRAND];

            if (brandField && brandName) {
                const brandMatch = brandField.choices.find((choice) => normalizeBrandName(choice.name) === normalizeBrandName(brandName));

                if (brandMatch) {
                    this.$set(this.selected, brandField.id, brandField.is_multi_select ? [brandMatch] : brandMatch);
                }
            }

            const brandStyleField = this.fields.find((field) => field.slug === ANSELL_FIELD_SLUGS.BRAND_STYLE);
            const brandStyle = template?.[ANSELL_DATA_KEYS.BRAND_STYLE_ITEM] || preview?.data?.[ANSELL_DATA_KEYS.BRAND_STYLE_ITEM];

            if (brandStyleField && brandStyle) {
                this.$set(this.selected, brandStyleField.id, brandStyle);
            }

            const industryField = this.fields.find((field) => field.slug === ANSELL_FIELD_SLUGS.INDUSTRIES_VERTICALS);
            const industryValue = template?.[ANSELL_DATA_KEYS.SUB_INDUSTRIES_EN_US] || preview?.data?.[ANSELL_DATA_KEYS.SUB_INDUSTRIES_EN_US];

            if (industryField && industryValue) {
                const industryNames = industryValue.split(',').map((name) => normalizeBrandName(name));
                const industryMatches = industryField.choices.filter((choice) => industryNames.includes(normalizeBrandName(choice.name)));

                if (industryMatches.length) {
                    this.$set(this.selected, industryField.id, industryField.is_multi_select ? industryMatches : industryMatches[0]);
                }
            }
        },

        getPreview() {
            const previewsList = window.previews || [];

            return previewsList.find((preview) => preview.id === this.instanceId) || previewsList[0];
        },

        updateItemToken() {
            const template = getOptimizelyTemplateByInstanceId(this.instanceId);
            const preview = this.getPreview();
            const projectName = (template?.project_name || preview?.data?.project_name)?.replace(/\s+/g, '_');

            const templateName = (template?.template_name || preview?.data?.template_name)?.replace(/\s+/g, '_');
            const itemName = projectName || templateName;

            const previous = this.titleTokenValues.item || '[Item]';

            if (itemName !== previous) {
                this.title = this.title.replace(previous, itemName);
                this.titleTokenValues.item = itemName;
            }
        },

        updateTitleTokens() {
            Object.entries(TITLE_TOKENS).forEach(([slug, { token, codes }]) => {
                const field = this.fields.find((f) => f.slug === slug);
                const value = field ? this.selected[field.id] : null;
                const names = Array.isArray(value) ? value.map((choice) => choice.name) : (value ? [value.name] : []);
                let resolved = names.map((name) => (codes ? nameToCode(name, codes) : name)).join('_');

                if (!resolved && slug === 'region') {
                    resolved = 'Global';
                }

                if (!resolved) {
                    return;
                }

                const previous = this.titleTokenValues[slug] || token;

                if (resolved !== previous) {
                    this.title = this.title.replace(previous, resolved);
                    this.titleTokenValues[slug] = resolved;
                }
            });
        },

        isPlainInput(field) {
            return field.type === 'text' || field.type === 'date';
        },

        getInstanceText() {
            const preview = this.getPreview();

            if (!preview?.canvas) {
                return '';
            }

            return preview.canvas.getObjects()
                .filter((el) => el?.templateField?.type === FIELD_TYPE_TEXT || el?.templateField?.type === FIELD_TYPE_MULTI_LINE_TEXT)
                .map((el) => el?.text || '')
                .join(' ')
                .trim();
        },

        async detectPrimaryLanguage() {
            const text = this.getInstanceText();

            if (!text) {
                return null;
            }

            const availability = await window.LanguageDetector.availability();

            if (availability === 'unavailable') {
                return null;
            }

            const detector = await window.LanguageDetector.create();
            const results = await detector.detect(text);

            return results?.[0]?.detectedLanguage?.split('-')[0]?.toLowerCase() || null;
        },

        async detectLanguageAndCountry() {
            this.detectingLanguage = true;

            try {
                const primary = await this.detectPrimaryLanguage();

                if (!primary) {
                    toastr.warning('Could not detect a language from this instance\'s text.');

                    return;
                }

                const languageField = this.fields.find((f) => f.slug === 'languages');
                const countryField = this.fields.find((f) => f.slug === 'country');
                let languageMatch = null;

                if (languageField) {
                    languageMatch = languageField.choices.find((choice) => nameToCode(choice.name, LANGUAGE_CODES).toLowerCase() === primary);

                    if (languageMatch) {
                        this.$set(this.selected, languageField.id, languageField.is_multi_select ? [languageMatch] : languageMatch);
                    }
                }

                if (countryField) {
                    const countryCode = defaultCountryForLanguage(primary);
                    const countryMatch = countryCode && countryField.choices.find((choice) => nameToCode(choice.name, COUNTRY_CODES).toLowerCase() === countryCode.toLowerCase());

                    if (countryMatch) {
                        this.$set(this.selected, countryField.id, countryField.is_multi_select ? [countryMatch] : countryMatch);
                    }
                }

                if (languageMatch) {
                    toastr.success(`Detected language: ${languageMatch.name}`);
                } else {
                    toastr.warning(`Detected language "${primary}" has no matching choice.`);
                }
            } catch (e) {
                toastr.error('Language detection failed.');
            } finally {
                this.detectingLanguage = false;
            }
        },

        serializeSelection(field) {
            const value = this.selected[field.id];

            if (this.isPlainInput(field)) {
                return JSON.stringify(value ? value.trim() : null);
            }

            if (field.is_multi_select) {
                return JSON.stringify((value || []).map((choice) => choice.id));
            }

            return JSON.stringify(value ? value.id : null);
        }
    }
}
</script>
